/**
 * Редактор поста — главный экран продукта.
 * Слева чат с Claude (живая строка «Что делает Claude»), справа превью ВК, ручная правка, фото и публикация.
 * На телефоне — вкладки «Чат · Превью · Фото и публикация».
 */
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import {
  ArrowLeft,
  CalendarClock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ImagePlus,
  Images,
  Link2,
  PenLine,
  Send,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from "lucide-react";
import type { JobSummary, PostFull } from "@impuls/shared";
import { MAX_IMAGES } from "@impuls/shared";
import { api, ApiError, errText } from "../lib/api";
import { ago, dateTime, domainOf, duration, fromLocalInput, humanDateTime, postponeSlot, toLocalInput } from "../lib/format";
import { prepareImage } from "../lib/images";
import { usePost, useProjects, useRefreshPosts, useSettings, useVkStatus } from "../lib/queries";
import { useJobStream } from "../lib/streams";
import { EcgLoader } from "../components/Ecg";
import { JobFeed, LiveLine, useElapsed } from "../components/JobFeed";
import { PhotoCredit, SourceTag, StatusChip } from "../components/PostBits";
import { PostponedModal } from "../components/PostponedModal";
import { VkPreview } from "../components/VkPreview";
import { EmptyState, LoadError, MASCOT, Modal, useConfirm, useToast } from "../components/ui";

type Pane = "chat" | "preview" | "media";

export function EditorPage() {
  const id = Number(useParams().id);
  const { data: post, error, refetch, isLoading } = usePost(id);
  const [pane, setPane] = useState<Pane>("chat");

  useEffect(() => {
    if (post) document.title = `${post.title} — Импульс · Контент-студия`;
  }, [post]);

  if (isLoading) {
    return (
      <div className="page-loading">
        <EcgLoader label="Открываю пост" />
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="page">
        {error instanceof ApiError && error.status === 404 ? (
          <EmptyState
            mascot="sad"
            title="Пост не найден"
            text="Возможно, его удалили."
            action={
              <Link className="btn" to="/write">
                К списку постов
              </Link>
            }
          />
        ) : (
          <LoadError error={error} retry={() => void refetch()} />
        )}
      </div>
    );
  }
  return (
    <div className="editor" data-pane={pane}>
      <EditorBar post={post} />
      <nav className="editor__tabs" aria-label="Части редактора">
        {(
          [
            ["chat", "Чат"],
            ["preview", "Превью"],
            ["media", "Фото и публикация"],
          ] as const
        ).map(([v, label]) => (
          <button key={v} className={`editor__tab ${pane === v ? "is-active" : ""}`} onClick={() => setPane(v)} aria-pressed={pane === v}>
            {label}
            {v === "media" && post.images.length > 0 && <span className="editor__tab-count">{post.images.length}</span>}
          </button>
        ))}
      </nav>
      <div className="editor__grid">
        <Chat post={post} />
        <div className="editor__side">
          <PreviewPane post={post} />
          <div className="editor__media">
            <Photos post={post} />
            <PublishBox post={post} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Шапка ================= */
function EditorBar({ post }: { post: PostFull }) {
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const refresh = useRefreshPosts();
  const qc = useQueryClient();
  const { data: projects } = useProjects("0");
  const [title, setTitle] = useState(post.title);
  useEffect(() => setTitle(post.title), [post.title]);
  const locked = post.status === "published";
  const isAuthor = post.source === "author";
  const back = isAuthor ? "/write" : "/auto";

  const save = async (patch: Record<string, unknown>) => {
    try {
      const p = await api.put<PostFull>(`/api/posts/${post.id}`, patch);
      qc.setQueryData(["post", post.id], p);
      refresh();
    } catch (e) {
      toast(errText(e), "error");
    }
  };
  const remove = async () => {
    const ok = await confirm({
      title: "Удалить пост?",
      text:
        post.status === "scheduled"
          ? `«${post.title}» будет удалён, а отложенная запись во ВКонтакте — снята.`
          : `«${post.title}» исчезнет вместе с чатом и фото.`,
      confirm: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/posts/${post.id}`);
      toast("Пост удалён");
      refresh();
      nav(back);
    } catch (e) {
      if (e instanceof ApiError && e.code === "vk_manual") {
        const force = await confirm({
          title: "Удалите запись во ВКонтакте вручную",
          text: e.message,
          confirm: "Я удалил во ВК — удалить пост",
          danger: true,
        });
        if (force) {
          await api.del(`/api/posts/${post.id}?force=1`).catch((err) => toast(errText(err), "error"));
          refresh();
          nav(back);
        }
      } else {
        // например, «Пост уже вышел во ВКонтакте» — сервер уже отметил его опубликованным
        toast(errText(e), "error");
        refresh(post.id);
      }
    }
  };
  const projectOptions = useMemo(() => {
    const list = projects ?? [];
    if (post.project_id && !list.some((p) => p.id === post.project_id))
      return [...list, { id: post.project_id, name: post.project_name ?? "Проект в архиве" }];
    return list;
  }, [projects, post.project_id, post.project_name]);

  return (
    <header className="editor__bar">
      <Link to={back} className="btn btn--ghost editor__back" aria-label="Назад к списку">
        <ArrowLeft size={18} />
        <span className="editor__back-text">Назад</span>
      </Link>
      <input
        className="editor__title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => title.trim() !== post.title && save({ title: title.trim() || "Без названия" })}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        disabled={locked}
        aria-label="Название поста"
      />
      <div className="editor__meta">
        <SourceTag source={post.source} international={post.international} />
        {/* у автопостов проект не выбирают — они всегда в общем */}
        {isAuthor && (
          <select
            className="select editor__project"
            value={post.project_id ?? ""}
            onChange={(e) => save({ project_id: e.target.value ? Number(e.target.value) : null })}
            disabled={locked}
            aria-label="Проект"
          >
            <option value="">Без проекта</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}
        <StatusChip status={post.status} at={post.scheduled_at} />
        <button className="icon-btn icon-btn--danger" onClick={remove} aria-label="Удалить пост" title="Удалить пост">
          <Trash2 size={19} />
        </button>
      </div>
    </header>
  );
}

/* ================= Чат ================= */
function Chat({ post }: { post: PostFull }) {
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = useRefreshPosts();
  const [msg, setMsg] = useState("");
  const [jobId, setJobId] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isAuto = post.source !== "author";
  const locked = post.status === "published";

  // после перезагрузки страницы подхватываем незавершённый ход
  const runningTurn = post.active_jobs.find((j) => j.type === "author_turn");
  useEffect(() => {
    if (runningTurn && !jobId) setJobId(runningTurn.id);
  }, [runningTurn, jobId]);

  const stream = useJobStream(jobId, (job: JobSummary) => {
    void qc.invalidateQueries({ queryKey: ["post", post.id] });
    refresh();
    if (job.status === "failed") toast(job.info, "error");
    setJobId(null);
  });
  const busy = !!jobId;

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (window.matchMedia("(max-width: 899px)").matches) el.lastElementChild?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    else el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [post.messages.length, busy, stream.events.length]);

  const send = async () => {
    const text = msg.trim();
    if (!text || busy) return;
    setMsg("");
    try {
      const r = await api.post<{ jobId: number; post: PostFull }>(`/api/posts/${post.id}/messages`, { text });
      qc.setQueryData(["post", post.id], r.post);
      setJobId(r.jobId);
    } catch (e) {
      setMsg(text);
      toast(errText(e), "error");
    }
  };

  return (
    <section className="chat" aria-label="Чат с Claude">
      <div className="chat__list" ref={listRef}>
        {isAuto && <AutoNote post={post} />}
        {!isAuto && post.messages.length === 0 && !busy && (
          <div className="chat__intro">
            <img src={MASCOT.wave} alt="" width={90} height={120} />
            <div>
              <h3>Расскажите, что произошло</h3>
              <p className="muted">Своими словами, можно сумбурно. Дальше просто просите правки.</p>
            </div>
          </div>
        )}
        {post.messages.map((m) => (
          <div key={m.id} className={`bubble bubble--${m.role} ${m.is_error ? "bubble--error" : ""}`}>
            {m.role === "assistant" && <img className="bubble__ava" src="/brand/heart-red.png" alt="" />}
            <div className="bubble__body">
              <div className="bubble__meta">
                <span>{m.role === "user" ? m.author_name || "Вы" : "Claude"}</span>
                <time>{ago(m.created_at)}</time>
              </div>
              <div className="bubble__text">{m.content}</div>
            </div>
          </div>
        ))}
        {busy && (
          <div className="bubble bubble--assistant bubble--live">
            <img className="bubble__ava" src="/brand/heart-red.png" alt="" />
            <div className="bubble__body">
              <LiveLine job={stream.job} events={stream.events} />
            </div>
          </div>
        )}
      </div>
      {locked ? (
        <div className="chat__locked">Пост опубликован — правки только во ВКонтакте.</div>
      ) : (
        <form
          className="chat__input"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            className="textarea"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={3}
            aria-label="Сообщение для Claude"
            placeholder={
              post.messages.length || isAuto
                ? "Что поправить? Например: «короче», «другой заголовок», «добавь смайлы»"
                : "Что произошло? Расскажите своими словами…"
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <div className="chat__actions">
            <span className="chat__hint">Ctrl + Enter — отправить</span>
            <button className="btn btn--primary" disabled={!msg.trim() || busy} aria-label="Отправить Claude">
              <Send size={17} />
              <span>Отправить</span>
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function AutoNote({ post }: { post: PostFull }) {
  const [how, setHow] = useState(false);
  return (
    <div className="autonote">
      <div className="autonote__head">
        <SourceTag source={post.source} international={post.international} />
        <span>Claude подготовил этот {post.source === "fact" ? "пост-факт" : post.source === "myth" ? "пост про миф" : "пост"} сам</span>
        {post.job_id && (
          <button className="autonote__how" onClick={() => setHow((x) => !x)} aria-expanded={how}>
            Как Claude это нашёл
            <ChevronDown size={15} className={how ? "is-open" : ""} aria-hidden="true" />
          </button>
        )}
      </div>
      {how && post.job_id && <HowFound jobId={post.job_id} />}
      {post.sources.length > 0 && (
        <div className="autonote__block">
          <h4>Источники</h4>
          <ul className="autonote__sources">
            {post.sources.map((s, i) => (
              <li key={i}>
                <Link2 size={14} aria-hidden="true" />
                <a href={s.url} target="_blank" rel="noreferrer">
                  {s.title || s.url}
                </a>
                <span className="muted small">{domainOf(s.url)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {post.checks.length > 0 && (
        <div className="autonote__block autonote__checks">
          <h4>Проверьте перед публикацией</h4>
          <ul>
            {post.checks.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Шаги задачи, которая подготовила пост: поиски, прочитанные страницы, мысли. */
function HowFound({ jobId }: { jobId: number }) {
  const { job, events, failed } = useJobStream(jobId);
  return (
    <div className="autonote__feed">
      {failed && !job ? <p className="muted small">Ход работы больше не хранится</p> : <JobFeed job={job} events={events} compact />}
    </div>
  );
}

/* ================= Превью и ручная правка ================= */
function PreviewPane({ post }: { post: PostFull }) {
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = useRefreshPosts();
  const { data: settings } = useSettings();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(post.text);
  // текст, с которого начали правку: если Claude его поменял — показываем новую версию рядом с черновиком
  const [base, setBase] = useState(post.text);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!editing) setText(post.text);
  }, [post.text, editing]);
  const locked = post.status === "published";
  const dirty = text !== post.text;
  const claudeBusy = post.active_jobs.some((j) => j.type === "author_turn");
  const theirs = editing && post.text !== base ? post.text : null;

  const startEdit = () => {
    setText(post.text);
    setBase(post.text);
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      // base_text — последняя версия, которую видел владелец; сервер ответит 409, если Claude успел раньше
      const p = await api.put<PostFull>(`/api/posts/${post.id}`, { text, base_text: post.text });
      qc.setQueryData(["post", post.id], p);
      refresh();
      setEditing(false);
      toast("Текст сохранён — Claude возьмёт его за основу в следующем ответе");
    } catch (e) {
      // Claude успел раньше: черновик остаётся в поле, его версия с тем же сообщением появится над ним
      if (e instanceof ApiError && e.code === "text_conflict") await qc.invalidateQueries({ queryKey: ["post", post.id] });
      else toast(errText(e), "error");
    } finally {
      setSaving(false);
    }
  };
  const when =
    post.status === "scheduled" && post.scheduled_at
      ? humanDateTime(post.scheduled_at * 1000)
      : post.status === "published" && post.published_at
        ? dateTime(post.published_at * 1000)
        : undefined;

  return (
    <section className="preview" aria-label="Превью во ВКонтакте">
      <div className="preview__head">
        <h2 className="preview__title">Превью ВКонтакте</h2>
        {!locked && !editing && post.text && (
          <button className="btn btn--ghost btn--small" onClick={startEdit}>
            <PenLine size={16} />
            Поправить вручную
          </button>
        )}
      </div>
      {editing ? (
        <div className="manual">
          {theirs !== null && (
            <div className="manual__theirs" role="alert">
              <div className="manual__theirs-head">
                <TriangleAlert size={16} aria-hidden="true" />
                <span>Claude уже обновил текст — посмотрите новую версию</span>
                <span className="grow" />
                <button
                  className="btn btn--ghost btn--small"
                  onClick={() => {
                    setText(theirs);
                    setBase(theirs);
                  }}
                >
                  Взять версию Claude
                </button>
              </div>
              <div className="manual__theirs-text" aria-label="Новая версия Claude">
                {theirs}
              </div>
            </div>
          )}
          <textarea
            className="textarea manual__area"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={18}
            aria-label="Текст поста"
            autoFocus
          />
          <div className="manual__bar">
            <span className="muted small">
              {text.length} знаков{text.length > 16000 ? " — ВКонтакте не примет больше 16 000" : ""}
            </span>
            <span className="grow" />
            <button
              className="btn btn--ghost"
              onClick={() => {
                setText(post.text);
                setEditing(false);
              }}
            >
              Отмена
            </button>
            <button className="btn btn--dark" onClick={save} disabled={!dirty || saving || claudeBusy || text.length > 16000}>
              {saving ? "Сохраняю…" : claudeBusy ? "Claude пишет…" : "Сохранить текст"}
            </button>
          </div>
        </div>
      ) : (
        <div className="vk-desk">
          <VkPreview
            text={post.text}
            images={post.images}
            groupName={settings?.vk_group_name || "Центр безопасности «Импульс»"}
            when={when}
          />
        </div>
      )}
    </section>
  );
}

/* ================= Фото ================= */
function Photos({ post }: { post: PostFull }) {
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = useRefreshPosts();
  const { data: settings } = useSettings();
  const gemini = settings?.image_provider === "gemini-api";
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [prompt, setPrompt] = useState(post.image_prompt ?? "");
  const [genJob, setGenJob] = useState<number | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const locked = post.status === "published";
  useEffect(() => setPrompt(post.image_prompt ?? ""), [post.image_prompt]);

  const runningImage = post.active_jobs.find((j) => j.type === "image");
  useEffect(() => {
    if (runningImage && !genJob) setGenJob(runningImage.id);
  }, [runningImage, genJob]);
  const genStream = useJobStream(genJob, (job) => {
    void qc.invalidateQueries({ queryKey: ["post", post.id] });
    refresh();
    toast(job.status === "done" ? "Картинка готова" : job.info, job.status === "done" ? "ok" : "error");
    setGenJob(null);
  });
  const genElapsed = useElapsed(genStream.job?.started_at, !!genJob);

  const apply = (p: PostFull) => {
    qc.setQueryData(["post", post.id], p);
    refresh();
  };

  const upload = async (files: FileList | File[]) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return toast("Перетащите фото: JPG, PNG или WEBP", "error");
    if (post.images.length + list.length > MAX_IMAGES) return toast(`ВКонтакте разрешает не больше ${MAX_IMAGES} фото в посте`, "error");
    setUploading(true);
    try {
      const prepared = await Promise.all(list.map(prepareImage));
      const fd = new FormData();
      prepared.forEach((f) => fd.append("files", f, f.name));
      apply(await api.post<PostFull>(`/api/posts/${post.id}/images`, fd));
      toast(list.length > 1 ? `Добавлено фото: ${list.length}` : "Фото добавлено");
    } catch (e) {
      toast(errText(e), "error");
    } finally {
      setUploading(false);
    }
  };
  // Ctrl+V на всей странице поста: блок с фото не держит фокус, поэтому слушаем документ.
  // Word и Excel кладут рядом с текстом его картинку — в поле ввода такая вставка остаётся текстом.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const clip = e.clipboardData;
      if (!clip || e.defaultPrevented) return;
      const images = Array.from(clip.files).filter((f) => f.type.startsWith("image/"));
      if (!images.length) return;
      const inField = e.target instanceof Element && e.target.closest("input, textarea, [contenteditable]") !== null;
      if (inField && clip.getData("text/plain").trim()) return;
      e.preventDefault();
      if (locked) toast("Пост уже вышел — фото не добавить", "error");
      else if (uploading) toast("Дождитесь загрузки фото", "error");
      else void upload(images);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  });
  const generate = async () => {
    try {
      const r = await api.post<{ jobId: number }>(`/api/posts/${post.id}/images/generate`, { prompt });
      setGenJob(r.jobId);
      setGenOpen(false);
    } catch (e) {
      toast(errText(e), "error");
    }
  };
  const del = async (imgId: number) => {
    try {
      apply(await api.del<PostFull>(`/api/images/${imgId}`));
    } catch (e) {
      toast(errText(e), "error");
    }
  };
  const order = async (ids: number[]) => {
    qc.setQueryData<PostFull>(["post", post.id], (p) => (p ? { ...p, images: ids.map((i) => p.images.find((x) => x.id === i)!) } : p));
    try {
      apply(await api.put<PostFull>(`/api/posts/${post.id}/images/order`, { ids }));
    } catch (e) {
      toast(errText(e), "error");
      void qc.invalidateQueries({ queryKey: ["post", post.id] }); // вернуть настоящий порядок
    }
  };
  const move = (idx: number, dir: -1 | 1) => {
    const ids = post.images.map((i) => i.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    void order(ids);
  };
  const dropOnThumb = (targetId: number) => {
    if (dragId === null || dragId === targetId) return;
    const ids = post.images.map((i) => i.id).filter((i) => i !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    setDragId(null);
    void order(ids);
  };
  const onPanelDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    if (!locked && dragId === null && e.dataTransfer.files.length) void upload(e.dataTransfer.files);
    setDragId(null);
  };

  return (
    <section
      className={`panel photos ${drag ? "photos--drag" : ""}`}
      aria-label="Фото"
      onDragOver={(e) => {
        // браузер считает перетаскиваемую миниатюру файлом: это смена порядка, а не загрузка
        if (!locked && dragId === null && e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDrag(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDrag(false);
      }}
      onDrop={onPanelDrop}
    >
      <div className="panel__head">
        <h3>
          Фото <span className="muted">{post.images.length} из 10</span>
        </h3>
        {!locked && (
          <div className="row">
            <button
              className="btn btn--small"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || post.images.length >= MAX_IMAGES}
            >
              <Upload size={16} />
              Загрузить
            </button>
            <button
              className="btn btn--small"
              onClick={() => setGenOpen((x) => !x)}
              disabled={!!genJob || post.images.length >= MAX_IMAGES}
              aria-expanded={genOpen}
            >
              <Sparkles size={16} />
              Нарисовать
            </button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) void upload(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {genOpen && (
        <div className="gen">
          <label className="field">
            <span>Что нарисовать</span>
            <textarea
              className="textarea"
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Например: подросток держит обожжённую руку под струёй холодной воды на кухне"
            />
          </label>
          <div className="row">
            <button className="btn btn--primary btn--small" onClick={generate}>
              <Sparkles size={16} />
              Нарисовать
            </button>
            <button className="btn btn--ghost btn--small" onClick={() => setGenOpen(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}
      {genJob && (
        <div className="photos__busy">
          <EcgLoader label={gemini ? "Gemini рисует картинку…" : "Claude рисует иллюстрацию…"} />
          <span className="muted small">{duration(genElapsed)}</span>
        </div>
      )}
      {uploading && (
        <div className="photos__busy">
          <EcgLoader label="Загружаю фото…" />
        </div>
      )}

      {post.images.length > 0 ? (
        <ol className="thumbs">
          {post.images.map((img, i) => (
            <li
              key={img.id}
              className={`thumb-cell ${dragId === img.id ? "is-dragging" : ""}`}
              draggable={!locked}
              onDragStart={(e) => {
                setDragId(img.id);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(img.id));
              }}
              onDragEnd={() => setDragId(null)}
              onDragOver={(e) => dragId !== null && e.preventDefault()}
              onDrop={(e) => {
                if (dragId !== null) {
                  e.preventDefault();
                  e.stopPropagation();
                  dropOnThumb(img.id);
                }
              }}
            >
              <div className="thumb">
                <img src={img.url} alt={`Фото ${i + 1}`} />
                {i === 0 && <span className="thumb__main">Главное</span>}
                {(img.origin === "gemini" || img.origin === "claude") && (
                  <span className="thumb__tag">{img.origin === "claude" ? "Claude" : "Gemini"}</span>
                )}
                {!locked && (
                  <div className="thumb__tools">
                    <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Сдвинуть раньше">
                      <ChevronLeft size={16} />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === post.images.length - 1} aria-label="Сдвинуть позже">
                      <ChevronRight size={16} />
                    </button>
                    <button onClick={() => void del(img.id)} aria-label="Удалить фото" className="thumb__del">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
              <PhotoCredit image={img} className="thumb__credit" />
            </li>
          ))}
        </ol>
      ) : (
        !locked &&
        !uploading &&
        !genJob && (
          <button className="dropzone" onClick={() => fileRef.current?.click()}>
            <ImagePlus size={28} strokeWidth={1.6} aria-hidden="true" />
            <span>Перетащите фото, вставьте через Ctrl+V или нажмите, чтобы выбрать</span>
            <span className="muted small">JPG, PNG, WEBP · до 10 фото</span>
          </button>
        )
      )}
    </section>
  );
}

/* ================= Публикация ================= */
/** Как на сервере: за минуту до выхода отложенную запись уже не трогаем — её выпускает ВК. */
const RELEASE_MARGIN_SEC = 60;

/** Время отложенного поста подошло (проверяем раз в 15 секунд). */
function useReleasing(post: PostFull): boolean {
  const at = post.status === "scheduled" ? post.scheduled_at : null;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (at === null) return;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [at]);
  return at !== null && (at - RELEASE_MARGIN_SEC) * 1000 <= nowMs;
}

function PublishBox({ post }: { post: PostFull }) {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const refresh = useRefreshPosts();
  const { data: vk } = useVkStatus(post.status !== "published");
  const { data: settings } = useSettings();
  const scheduledInput = post.status === "scheduled" && post.scheduled_at ? toLocalInput(new Date(post.scheduled_at * 1000)) : null;
  const [when, setWhen] = useState(scheduledInput ?? postponeSlot());
  // поле не трогали — время считаем в момент нажатия (страница могла простоять открытой час)
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState<"" | "schedule" | "unschedule">("");
  const [manual, setManual] = useState<string | null>(null);
  const [photosOpen, setPhotosOpen] = useState(false);
  const releasing = useReleasing(post);
  useEffect(() => {
    if (scheduledInput) {
      setWhen(scheduledInput);
      setTouched(false);
    }
  }, [scheduledInput]);
  // пока ВК выпускает запись, раз в минуту спрашиваем сервер — планировщик отметит пост опубликованным
  useEffect(() => {
    if (!releasing) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: ["post", post.id] }), 60_000);
    return () => clearInterval(t);
  }, [releasing, qc, post.id]);

  const apply = (p: PostFull) => {
    qc.setQueryData(["post", post.id], p);
    refresh(post.id);
  };

  if (post.status === "published") {
    const groupId = (settings?.vk_group_id || "").replace(/\D/g, "");
    return (
      <section className="panel publish publish--done" aria-label="Публикация">
        <div className="publish__done">
          <span className="chip chip--ok">Опубликован</span>
          <span>{post.published_at ? dateTime(post.published_at * 1000) : ""}</span>
        </div>
        {/* у вышедшей отложенной записи ВК новый id; если его не узнать — ведём на стену */}
        {post.vk_url ? (
          <a className="btn btn--dark" href={post.vk_url} target="_blank" rel="noreferrer">
            <ExternalLink size={17} />
            Открыть во ВКонтакте
          </a>
        ) : (
          groupId && (
            <a className="btn btn--dark" href={`https://vk.ru/club${groupId}`} target="_blank" rel="noreferrer">
              <ExternalLink size={17} />
              Открыть стену сообщества
            </a>
          )
        )}
      </section>
    );
  }

  const handle = async (fn: () => Promise<void>, kind: typeof busy) => {
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      if (e instanceof ApiError && e.code === "vk_manual") setManual(e.message);
      else {
        setManual(null);
        // «Пост уже вышел во ВКонтакте» — не сбой, сервер уже отметил пост опубликованным
        toast(errText(e), e instanceof ApiError && e.code === "vk_released" ? "info" : "error");
      }
      refresh(post.id);
    } finally {
      setBusy("");
    }
  };
  const notConnected = vk && (vk.mode === "none" || (!vk.ok && !vk.mock));
  // подключено, но ВК не даёт публиковать (например, вход VK ID без права на стену)
  const cannotPost = vk && !notConnected && !vk.can_post;
  const scheduled = post.status === "scheduled";
  const schedule = () =>
    handle(async () => {
      if (!post.text.trim()) throw new Error("Сначала нужен текст поста");
      // время в поле — в поясе платформы, а не браузера
      const at = fromLocalInput(touched || scheduled ? when : postponeSlot());
      if (!Number.isFinite(at)) throw new Error("Укажите дату и время");
      const r = await api.post<{ post: PostFull; warning: string | null }>(`/api/posts/${post.id}/publish`, { at });
      apply(r.post);
      if (r.warning) toast(r.warning, "info");
      else if (scheduled) toast(`Выйдет ${dateTime(at * 1000)}`);
      // сразу показываем, что делать дальше: скачать фото и добавить их в запись во ВК
      if (!scheduled || r.post.images.length) setPhotosOpen(true);
    }, "schedule");
  const unschedule = (force = false) =>
    handle(async () => {
      if (!force) {
        const ok = await confirm({
          title: "Снять с публикации?",
          text: "Отложенная запись во ВКонтакте будет удалена, пост вернётся в черновики.",
          confirm: "Снять с публикации",
        });
        if (!ok) return;
      }
      apply(await api.post<PostFull>(`/api/posts/${post.id}/unschedule`, force ? { force: true } : {}));
      setManual(null);
      toast("Снято с публикации");
    }, "unschedule");

  return (
    <section className="panel publish" aria-label="Публикация">
      <div className="panel__head">
        <h3>{scheduled ? "В отложенных ВК" : "Публикация"}</h3>
        {scheduled && post.scheduled_at && <span className="chip chip--wine">{humanDateTime(post.scheduled_at * 1000)}</span>}
      </div>
      {post.error && (
        <div className="error-line" role="alert">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>{post.error}</span>
        </div>
      )}
      {scheduled && post.vk_dirty && !releasing && (
        <div className="note-line">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>Правки ещё не во ВКонтакте — нажмите «Обновить во ВК».</span>
        </div>
      )}
      {notConnected && (
        <div className="note-line">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>
            ВКонтакте не подключён. <Link to="/settings#vk">Подключить в Настройках</Link>
          </span>
        </div>
      )}
      {cannotPost && (
        <div className="note-line" role="status">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>
            {vk?.message ?? "ВКонтакте не даёт публиковать с текущим входом."} <Link to="/settings#vk">Настройки → ВКонтакте</Link>
          </span>
        </div>
      )}
      {releasing ? (
        // ВК выпускает запись сам: снять, перенести или выложить ещё раз уже нельзя — вышел бы дубль
        <div className="publish__releasing" role="status">
          <CalendarClock size={18} aria-hidden="true" />
          <span>Выходит во ВКонтакте…</span>
        </div>
      ) : (
        <>
          {scheduled && (
            <button className="btn btn--primary publish__photos" onClick={() => setPhotosOpen(true)}>
              <Images size={17} aria-hidden="true" />
              {post.images.length && !post.photos_in_vk ? "Фото и ссылка на пост" : "Ссылка на пост во ВК"}
            </button>
          )}
          <div className="publish__row">
            <label className="field publish__when">
              <span>Время выхода</span>
              <input
                className="input"
                type="datetime-local"
                value={when}
                onChange={(e) => {
                  setWhen(e.target.value);
                  setTouched(true);
                }}
              />
            </label>
            <button
              className={`btn ${scheduled ? "btn--dark" : "btn--primary"}`}
              onClick={() => void schedule()}
              disabled={!!busy || !post.text.trim()}
            >
              {scheduled ? <CalendarClock size={18} aria-hidden="true" /> : <Send size={17} aria-hidden="true" />}
              {busy === "schedule" ? "Отправляю…" : scheduled ? (post.vk_dirty ? "Обновить во ВК" : "Перенести") : "В отложенные"}
            </button>
          </div>
          {scheduled && (
            <div className="publish__row publish__row--second">
              <button className="btn btn--ghost" onClick={() => void unschedule()} disabled={!!busy}>
                {busy === "unschedule" ? "Снимаю…" : "Снять с публикации"}
              </button>
            </div>
          )}
        </>
      )}
      {scheduled && <PostponedModal post={post} open={photosOpen} onClose={() => setPhotosOpen(false)} />}
      <Modal
        open={!!manual}
        onClose={() => setManual(null)}
        title="Удалите запись во ВКонтакте вручную"
        footer={
          <>
            <button className="btn btn--ghost" onClick={() => setManual(null)}>
              Отмена
            </button>
            <button className="btn btn--dark" onClick={() => void unschedule(true)}>
              Я удалил во ВК
            </button>
          </>
        }
      >
        <p>{manual}</p>
        <p className="muted small">После этого пост вернётся в черновики, и его можно запланировать заново.</p>
      </Modal>
    </section>
  );
}
